import { useState } from 'react';
import { Zap } from '@/components/ui/icons';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UpgradeModal } from '@/components/UpgradeModal';
import { AuthPopup } from '@/components/auth/AuthPopup';

interface GenerationGateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * 'guest'      — visitor isn't signed in; route them to sign up.
   * 'no-credits' — signed-in user with a zero balance; route them to upgrade.
   */
  mode: 'guest' | 'no-credits';
}

/**
 * Replaces the silently-disabled Generate control when generation is blocked
 * (#176). Instead of a dead button, the user gets the correct next step: an
 * inline signup/login popup (guest) or upgrade (out of credits). The guest path
 * now signs up / logs in *in place* via `AuthPopup` — no bounce to /signup — so
 * the user keeps their prompt. The out-of-credits path hands off to the existing
 * `UpgradeModal` subscription surface.
 */
export function GenerationGateModal({
  open,
  onOpenChange,
  mode,
}: GenerationGateModalProps) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // The upgrade path hands off to the existing UpgradeModal. Render it
  // separately so it survives this gate closing.
  if (mode === 'no-credits') {
    return (
      <>
        <Dialog open={open && !upgradeOpen} onOpenChange={onOpenChange}>
          <DialogContent className="w-[95vw] max-w-md border-telekinesis-neutral-800 bg-telekinesis-bg-secondary-dark p-8 text-telekinesis-neutral-10 sm:rounded-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
                <Zap
                  className="h-5 w-5 text-telekinesis-gold"
                  fill="currentColor"
                />
                You&apos;re out of credits
              </DialogTitle>
              <DialogDescription className="text-sm text-telekinesis-neutral-400">
                You&apos;ve used all of your generation credits. Upgrade your
                plan for more credits and keep building.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4">
              <Button
                onClick={() => setUpgradeOpen(true)}
                className="h-10 w-full rounded-full bg-telekinesis-neutral-10 text-sm font-medium text-telekinesis-bg-dark [@media(hover:hover)]:hover:bg-white"
              >
                Upgrade
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <UpgradeModal
          open={upgradeOpen}
          onOpenChange={(next) => {
            setUpgradeOpen(next);
            // Closing the upgrade modal closes the whole gate flow.
            if (!next) onOpenChange(false);
          }}
        />
      </>
    );
  }

  // mode === 'guest' — sign up / log in in place, keeping the user's prompt.
  return (
    <AuthPopup open={open} onOpenChange={onOpenChange} initialMode="signup" />
  );
}
